import AddressInput from 'components/forms/inputs/rhf-inputs/AddressInput';
import MultiSelectInput from 'components/forms/inputs/rhf-inputs/MultiSelectInput';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import { isValidEmail } from 'components/forms/inputs/rhf-validators/validators';
import { FuzzySearchTableView, useFuzzySearch } from 'hooks/useFuzzySearch';
import { Control, UseFormSetValue, useWatch } from 'react-hook-form';
import { useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees.ts';
import { ClassifierData } from 'types/classifiers';
import { ContactForm, PartyType, RelationshipTypeCode } from 'types/contacts';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { t } from 'utils/getTranslations';
import DynamicMultiSelectInput from '../../forms/inputs/rhf-inputs/DynamicMultiSelectInput.tsx';
import PhoneNumberInput from '../../forms/inputs/rhf-inputs/PhoneNumberInput';
import SelectInput from '../../forms/inputs/rhf-inputs/SelectInput.tsx';
import InfoTooltip from '../../InfoTooltip.tsx';
import { contactKind, ListedIn, Setting } from 'types/back-office-settings';
import { useRequiredFields } from 'utils/useRequiredFields';
import { useSettings } from 'utils/useSettings';

interface Props {
    classifiers: Record<string, ClassifierData>;
    control: Control<ContactForm>;
    setValue: UseFormSetValue<ContactForm>;
    prefillName: (val: string | undefined | null) => void;
    requireEmail?: boolean;
    relationshipType?: string;
    fuzzySearch: ReturnType<typeof useFuzzySearch>;
    isCorrespondence?: boolean;
}

export const AddPartyCompanyForm = ({
    classifiers,
    control,
    setValue,
    requireEmail,
    prefillName,
    relationshipType,
    fuzzySearch,
    isCorrespondence = false,
}: Props) => {
    const { fuzzySearchStates, updateSearchState, searchHandlers } = fuzzySearch;
    const isCorrespondenceAddressEnabled = useSettings().isSettingEnabled(Setting.CORRESPONDENCE_ADDRESS);
    // The Client form's Required fields card holds for the fields this window shows, beside its own correspondence rules.
    const { isRequired } = useRequiredFields();
    const kind = contactKind(PartyType.COMPANY, relationshipType as RelationshipTypeCode | undefined);

    const watchedRegistrationAddress = useWatch({ control, name: 'registrationAddress' });
    const watchedCorrespondenceAddress = useWatch({ control, name: 'correspondenceAddress' });

    const needsConditionalAddressValidation = isCorrespondence && relationshipType !== RelationshipTypeCode.CLIENT;
    const isRegistrationAddressFilled = !!watchedRegistrationAddress?.address1;
    const isCorrespondenceAddressFilled = !!watchedCorrespondenceAddress?.address1;

    return (
        <>
            <div className="flex flex-col gap-4">
                <TextInput
                    name="officialName"
                    control={control}
                    label="contacts.company.name"
                    placeholder="contacts.company.name"
                    required
                    maxLength={150}
                    onChange={searchHandlers?.name?.handler}
                    onBlur={prefillName}
                />
                <TextInput
                    name="name"
                    control={control}
                    label="contacts.unofficial-name"
                    placeholder="forms.enter-name"
                    tooltip={<InfoTooltip text="contacts.tooltips.unofficial-name" />}
                    required
                    maxLength={150}
                />
                <FuzzySearchTableView
                    fuzzySearchResults={fuzzySearchStates.NAME.results}
                    partyType={PartyType.COMPANY}
                    isAccordionExpanded={fuzzySearchStates.NAME.isExpanded}
                    setIsAccordionExpanded={(expanded) => updateSearchState('NAME', { isExpanded: expanded })}
                    classifiers={classifiers}
                />
                {relationshipType &&
                (relationshipType === RelationshipTypeCode.CLIENT || relationshipType === RelationshipTypeCode.LEAD) ? (
                    <>
                        <DynamicMultiSelectInput
                            setValue={setValue}
                            control={control}
                            label="contacts.about.relationship-partner"
                            name="partners"
                            paginated={{
                                fetch: useLazyGetEmployeesListWithFiltersQuery,
                                searchField: 'fullName',
                                additionalParams: { extraParams: { listedIn: ListedIn.CONTACT_RELATIONSHIP_PARTNER } },
                            }}
                            getOptionLabel="name"
                            getOptionIdentifier="id"
                            placeholder="contacts.about.relationship-partner"
                            required={
                                relationshipType === RelationshipTypeCode.CLIENT ||
                                (isCorrespondence && relationshipType === RelationshipTypeCode.LEAD)
                            }
                        />
                        <DynamicMultiSelectInput
                            setValue={setValue}
                            control={control}
                            label="contacts.about.relationship-manager"
                            name="managers"
                            paginated={{
                                fetch: useLazyGetEmployeesListWithFiltersQuery,
                                searchField: 'fullName',
                                additionalParams: { extraParams: { listedIn: ListedIn.CONTACT_RELATIONSHIP_MANAGER } },
                            }}
                            getOptionLabel="name"
                            getOptionIdentifier="id"
                            placeholder="contacts.about.relationship-manager"
                            required={
                                isRequired('CLIENT', 'RELATIONSHIP_MANAGER', kind) ||
                                (isCorrespondence && relationshipType === RelationshipTypeCode.LEAD)
                            }
                        />
                    </>
                ) : null}
                <TextInput
                    name="regCode"
                    control={control}
                    label="reg-number"
                    placeholder="reg-number"
                    required={isRequired('CLIENT', 'REGISTRATION_NUMBER', kind)}
                    maxLength={20}
                    onChange={searchHandlers?.regCode?.handler}
                />
                <FuzzySearchTableView
                    fuzzySearchResults={fuzzySearchStates.REGISTRATION_NUMBER.results}
                    partyType={PartyType.COMPANY}
                    isAccordionExpanded={fuzzySearchStates.REGISTRATION_NUMBER.isExpanded}
                    setIsAccordionExpanded={(expanded) =>
                        updateSearchState('REGISTRATION_NUMBER', { isExpanded: expanded })
                    }
                    classifiers={classifiers}
                />
                {relationshipType && relationshipType === RelationshipTypeCode.CLIENT ? (
                    <SelectInput
                        setValue={setValue}
                        control={control}
                        label="contacts.billing.invoice-lang"
                        name="preferredLanguage"
                        options={
                            (getValidOptionsFromClassifier(classifiers['LANGUAGE']) as {
                                label: string;
                                value: string;
                            }[]) ?? []
                        }
                        getOptionLabel="label"
                        getOptionIdentifier="value"
                        placeholder="contacts.billing.invoice-lang"
                        required
                    />
                ) : null}
                {relationshipType &&
                (relationshipType === RelationshipTypeCode.CLIENT || relationshipType === RelationshipTypeCode.LEAD) ? (
                    <MultiSelectInput
                        control={control}
                        name="businessAreas"
                        label="business-areas"
                        placeholder="business-areas"
                        options={
                            (getValidOptionsFromClassifier(classifiers['BUSINESS_AREA_TYPE']) as {
                                label: string;
                                value: string | number;
                            }[]) ?? []
                        }
                        setValue={setValue}
                        getOptionLabel="label"
                        getOptionIdentifier="value"
                        sortDirection="asc"
                        required={isRequired('CLIENT', 'BUSINESS_AREAS', kind)}
                    />
                ) : null}
                <AddressInput
                    label="registration-address"
                    name="registrationAddress"
                    control={control}
                    classifiers={classifiers}
                    clearable
                    required={
                        relationshipType === RelationshipTypeCode.CLIENT ||
                        (needsConditionalAddressValidation && !isCorrespondenceAddressFilled)
                    }
                />
                {(isCorrespondenceAddressEnabled || isCorrespondence) && (
                    <AddressInput
                        label="contacts.correspondence-address"
                        name="correspondenceAddress"
                        control={control}
                        classifiers={classifiers}
                        clearable
                        required={needsConditionalAddressValidation && !isRegistrationAddressFilled}
                    />
                )}
            </div>
            <PhoneNumberInput name="mainPhone" control={control} label="phone" placeholder="phone" />
            <TextInput
                name="mainEmail"
                control={control}
                label="email"
                placeholder="email"
                maxLength={255}
                validate={(val) => isValidEmail(val, t('validation.email-must-be-valid'))}
                required={requireEmail}
            />
            <TextInput name="website" control={control} label="website" placeholder="website" />
        </>
    );
};
